import { anyLeague } from "../../classificators/league/index.js";
import { RankingStat } from "../../entities/index.js";
import ResultPair from '../../valueObjects/ResultPair.js';
import { BattingRankingStats, DataSection, HitData, PitchingRankingStats, RankingScopeContext, ResultDataNew } from "../../enums/index.js";
import MultiRankingStatCalculator from "./MultiRankingStatCalculator.js";
import NumberFormatter from "../../formatters/NumberFormatter.js";
import sessionWithHittingRadarData from "../../classificators/session/sessionWithHittingRadarData.js";
import ResultsSlice from "../../valueObjects/ResultsSlice.js";
import resultsSliceForPitchType from "../../classificators/resultsSlice/resultsSliceForPitchType.js";
import satisfiesAll from "../../classificators/satisfiesAll.js";
import battingScopeContext from "../../classificators/resultsSlice/battingScopeContext.js";
import pitchingScopeContext from "../../classificators/resultsSlice/pitchingScopeContext.js";
import RankingStatCaseVerifier from "../../validators/RankingStatCaseVerifier.js";
import classifiesAsHardHit from "../../classificators/hardHitClassifier.js";
import ClassifiedAsBattedBallEventFilter from "../../filters/contextual/ClassifiedAsBattedBallEventFilter.js";
import ContextualFilterBase from "../../filters/contextual/ContextualFilterBase.js";

class HardHitRateCalculator extends MultiRankingStatCalculator{
    constructor(context, hardHitClassifier, rankingStatCaseVerifier, numberFormatter, contextualResultFilter, skipCache) {
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(hardHitClassifier) !== 'undefined' && typeof(hardHitClassifier) !== 'function') {
            throw new TypeError('hardHitClassifier must be na predicate taking single argument - exit speed in m/s or undefined, to use default implementation');
        }
        if (typeof(contextualResultFilter) !== 'undefined' && !(contextualResultFilter instanceof ContextualFilterBase)) {
            throw new TypeError('contextualResultFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        const caseVerifier = rankingStatCaseVerifier || new RankingStatCaseVerifier(
            anyLeague,
            sessionWithHittingRadarData,
            satisfiesAll(
                context === RankingScopeContext.Pitching.key 
                    ? pitchingScopeContext
                    : battingScopeContext
                ,
                resultsSliceForPitchType
            )
        );
        super(
            context === RankingScopeContext.Pitching.key
                ? [ PitchingRankingStats.HardHitsAllowed, PitchingRankingStats.HardHitsRateAllowed ]
                : [ BattingRankingStats.HardHits, BattingRankingStats.HardHitRate ]
            ,
            caseVerifier,
            skipCache
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.hardHitClassifier = hardHitClassifier || classifiesAsHardHit;
        this.contextualFilter = contextualResultFilter || new ClassifiedAsBattedBallEventFilter();
        this.context = context;
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        const results = slice.getSmallestSlice();
        const bbEvents = this.filterResults(results, session, league);

        if (!bbEvents || bbEvents.length === 0) {
            return null;
        }

        let harHits = 0;
        let measuredBattedBalls = 0;

        for (let i = 0; i < bbEvents.length; i++) {
            const result = bbEvents[i];

            if (
                !result || typeof(result) !== 'object'
                || !result[ResultDataNew.Data.key]
                || !result[ResultDataNew.Data.key][DataSection.Sensor.key]
                || !result[ResultDataNew.Data.key][DataSection.Sensor.key][ResultPair.getHitProperty()]
                || !result[ResultDataNew.Data.key][DataSection.Sensor.key][ResultPair.getHitProperty()][HitData.ExitSpeed.key]
            ) {
                continue;
            }

            const exitSpeed = result[ResultDataNew.Data.key][DataSection.Sensor.key][ResultPair.getHitProperty()][HitData.ExitSpeed.key];
            if (exitSpeed === null || typeof(exitSpeed) === 'undefined' || Number.isNaN(exitSpeed)) {
                continue;
            }

            measuredBattedBalls++;

            if (this.hardHitClassifier(exitSpeed)) {
                harHits++
            }
        }

        if (!measuredBattedBalls) {
            return null;
        }

        const output = {};
        if (harHits) {
            output[this.supportedParams[0].key] = RankingStat.fromParameter(
                this.supportedParams[0],
                this.numberFormatter.format(harHits),
                harHits
            );
        }

        output[this.supportedParams[1].key] = RankingStat.fromParameter(
            this.supportedParams[1],
            this.numberFormatter.format((harHits / measuredBattedBalls) * 100),
            measuredBattedBalls
        );

        return output;
    }

    filterResults(results, session, league) {
        return this.contextualFilter.filter(results, session, league);
    }
}

export default HardHitRateCalculator;